export const ROUTES = {
  HOME: {
    path: "/",
    name: "Home",
    title: "LicoMeds - Mobile Healthcare with Fee Cashback Rewards",
    description:
      "Nigeria's premier mobile healthcare platform where patients earn up to 50% cashback on transaction fees. Connect with 800+ verified doctors, labs, and pharmacies through our mobile app with transparent fee structures.",
  },
  ABOUT: {
    path: "/about",
    name: "About Us",
    title: "About LicoMeds - Mobile Healthcare with Fee Cashback System",
    description:
      "Learn about LicoMeds' mission to transform African healthcare through our mobile-first platform connecting patients, doctors, labs, and pharmacies with transparent fee cashback rewards and community support.",
  },
  FEATURES: {
    path: "/features",
    name: "Features",
    title: "Mobile App Features - LicoMeds Healthcare Platform",
    description:
      "Discover comprehensive mobile healthcare features with fee cashback rewards designed for patients, doctors, labs, and pharmacies across Africa.",
  },
  FAQ: {
    path: "/faq",
    name: "FAQ",
    title: "Frequently Asked Questions - LicoMeds",
    description:
      "Get answers to common questions about our mobile healthcare platform, fee cashback system, and community support features.",
  },
  CONTACTUS: {
    path: "/contact-us",
    name: "Contact Us",
    title: "Contact LicoMeds - Mobile Healthcare Support",
    description:
      "Get in touch with our support team for questions about our mobile healthcare platform, fee cashback system, or community features.",
  },

  // Legal and Support pages
  PRIVACY: {
    path: "/privacy",
    name: "Privacy Policy",
    title: "Privacy Policy - LicoMeds Mobile Healthcare Platform",
    description:
      "How we protect your personal information and health data on our mobile healthcare platform with military-grade encryption.",
  },
  TERMS: {
    path: "/terms",
    name: "Terms & Conditions",
    title: "Terms and Conditions - LicoMeds",
    description:
      "Terms and conditions for using LicoMeds mobile healthcare platform, fee cashback system, and community support features.",
  },
  DONATE: {
    path: "/donate",
    name: "Make a Donation",
    title: "Donate - Support Healthcare Access for All",
    description:
      "Help provide healthcare access to those who need it most. Secure donations through trusted fintech partners or use your mobile app fee cashback to help others.",
  },
  REQUEST_HELP: {
    path: "/request-help",
    name: "Request Medical Aid",
    title: "Request Medical Aid - LicoMeds Community Support",
    description:
      "Get community support for medical expenses through our mobile app aid system.",
  },
  BLOG: {
    path: "/blog",
    name: "Blog",
    title: "Healthcare Blog - LicoMeds Insights & Expert Advice",
    description:
      "Stay informed with the latest healthcare trends, expert medical advice, and insights into mobile healthcare technology across Africa. Discover tips for better health management.",
  },
  BLOG_POST: {
    path: "/blog/:slug",
    name: "Blog Post",
    title: "Healthcare Article | Nigerian Medical Insights",
    description:
      "Read expert healthcare insights from Nigerian medical professionals on mobile health, wellness, and medical advice for African communities.",
  },
  BLOG_CATEGORY: {
    path: "/blog/category/:category",
    name: "Blog Category",
    title: "Healthcare Articles by Category | LicoMeds Blog",
    description:
      "Browse healthcare articles by category including telemedicine, wellness, medical advice, and community health insights from Nigerian experts.",
  },
  BLOG_AUTHOR: {
    path: "/blog/author/:author",
    name: "Blog Author",
    title: "Articles by Author | Nigerian Healthcare Experts",
    description:
      "Read healthcare articles from our expert Nigerian medical professionals and specialists.",
  },

  // Homepage sections (for smooth scrolling)
  SECTIONS: {
    HOME_HERO: "#home",
    ABOUT_US: "#about",
    SERVICES: "#services",
    APP_DOWNLOAD: "#app",
    COMMUNITY: "#community",
    STATS: "#stats",
    TESTIMONIALS: "#testimonials",
    CONTACT_CTA: "#contact",
    // About page sections
    ABOUT_MISSION: "#mission",
    ABOUT_ECOSYSTEM: "#ecosystem",
    ABOUT_TECHNOLOGY: "#technology",
  },

  // External links
  EXTERNAL: {
    APP_STORE: "https://apps.apple.com/app/licomeds/id123456789",
    GOOGLE_PLAY:
      "https://play.google.com/store/apps/details?id=com.nanocodes.licomed&hl=en",

    // Social media
    FACEBOOK: "https://facebook.com/licomeds",
    INSTAGRAM: "https://instagram.com/licomeds",
    TWITTER: "https://twitter.com/licomeds",
    TIKTOK: "https://tiktok.com/@licomeds",

    // Contact
    EMERGENCY_HOTLINE: "tel:+2349078801125",
    EMAIL: "mailto:info@licomeds.com",
    EMAIL_DISPLAY: "info@licomeds.com",
    EMERGENCY_DISPLAY: "+234 907 880 1125",
  },

  // Essential company info
  COMPANY: {
    NAME: "LicoMeds",
    FULL_NAME: "LicoMeds Healthcare Platform",
    TAGLINE: "Healthcare Platform",
    DESCRIPTION:
      "Nigeria's premier mobile healthcare platform where patients earn up to 50% cashback on platform transaction fees. All healthcare services - consultations, labs, pharmacy - happen in our mobile app with transparent fee structures and automatic rewards.",
  },
} as const;

// Type definitions
export type RouteInfo = {
  path: string;
  name: string;
  title: string;
  description: string;
};

export type NavItemType = "link" | "section" | "dropdown";

export type NavItem = {
  label: string;
  route?: RouteInfo | string;
  icon: string;
  type: NavItemType;
  description?: string;
  children?: Omit<NavItem, "children">[];
};

export type FooterNavItem = {
  label: string;
  route: RouteInfo | string;
  type: "link" | "section";
};

export type PageMetadata = {
  title: string;
  description: string;
  keywords: string;
  ogImage: string;
};

// Navigation menu structure
export const NAVIGATION_MENU: NavItem[] = [
  {
    label: "Home",
    route: ROUTES.HOME,
    icon: "fas fa-home",
    type: "link",
  },
  {
    label: "About",
    route: ROUTES.ABOUT,
    icon: "fas fa-info-circle",
    type: "link",
  },
  {
    label: "Features",
    route: ROUTES.FEATURES,
    icon: "fas fa-star",
    type: "link",
  },
  {
    label: "Community",
    icon: "fas fa-hands-helping",
    type: "dropdown",
    children: [
      {
        label: "Make a Donation",
        route: ROUTES.SECTIONS.COMMUNITY,
        icon: "fas fa-heart",
        type: "section",
        description: "Support healthcare access for those in need",
      },
      {
        label: "Request Medical Aid",
        route: ROUTES.SECTIONS.COMMUNITY,
        icon: "fas fa-hands-helping",
        type: "section",
        description: "Get help through our mobile app community",
      },
    ],
  },
  {
    label: "Support",
    icon: "fas fa-question-circle",
    type: "dropdown",
    children: [
      {
        label: "FAQ",
        route: ROUTES.FAQ,
        icon: "fas fa-question-circle",
        type: "link",
        description: "Frequently asked questions",
      },
      {
        label: "Contact Us",
        route: ROUTES.SECTIONS.CONTACT_CTA,
        icon: "fas fa-envelope",
        type: "section",
        description: "Get in touch with our team",
      },
      {
        label: "Emergency Support",
        route: ROUTES.EXTERNAL.EMERGENCY_HOTLINE,
        icon: "fas fa-phone",
        type: "link",
        description: "24/7 emergency medical assistance",
      },
    ],
  },
];

// Footer navigation structure
export const FOOTER_NAVIGATION: Record<string, FooterNavItem[]> = {
  Company: [
    {
      label: "About Us",
      route: ROUTES.ABOUT,
      type: "link",
    },
    {
      label: "Our Mission",
      route: ROUTES.SECTIONS.ABOUT_US,
      type: "section",
    },
    {
      label: "Contact Email",
      route: ROUTES.EXTERNAL.EMAIL,
      type: "link",
    },
  ],
  "Mobile App": [
    {
      label: "Download App",
      route: ROUTES.SECTIONS.APP_DOWNLOAD,
      type: "section",
    },
    {
      label: "App Features",
      route: ROUTES.FEATURES,
      type: "link",
    },
    {
      label: "Healthcare Services",
      route: ROUTES.SECTIONS.SERVICES,
      type: "section",
    },
  ],
  Community: [
    {
      label: "Make a Donation",
      route: ROUTES.SECTIONS.COMMUNITY,
      type: "section",
    },
    {
      label: "Request Medical Aid",
      route: ROUTES.SECTIONS.COMMUNITY,
      type: "section",
    },
    {
      label: "Success Stories",
      route: ROUTES.SECTIONS.TESTIMONIALS,
      type: "section",
    },
  ],
  Support: [
    {
      label: "FAQ",
      route: ROUTES.FAQ,
      type: "link",
    },
    {
      label: "Contact Us",
      route: ROUTES.SECTIONS.CONTACT_CTA,
      type: "section",
    },
    {
      label: "Emergency Hotline",
      route: ROUTES.EXTERNAL.EMERGENCY_HOTLINE,
      type: "link",
    },
  ],
};

// Call-to-action buttons configuration
export const CTA_BUTTONS = {
  DOWNLOAD_APP: {
    primary: {
      text: "Download App",
      icon: "fas fa-mobile-alt",
      action: "download_modal" as const,
    },
    ios: {
      text: "Download on App Store",
      icon: "fab fa-apple",
      url: ROUTES.EXTERNAL.APP_STORE,
    },
    android: {
      text: "Get it on Google Play",
      icon: "fab fa-google-play",
      url: ROUTES.EXTERNAL.GOOGLE_PLAY,
    },
  },
  DONATE: {
    text: "Donate Now",
    icon: "fas fa-heart",
    route: ROUTES.SECTIONS.COMMUNITY,
    priority: "medium" as const,
  },
  REQUEST_HELP: {
    text: "Request Help",
    icon: "fas fa-hands-helping",
    route: ROUTES.SECTIONS.COMMUNITY,
    priority: "medium" as const,
  },
} as const;

// Page metadata for SEO
export const PAGE_METADATA: Record<string, PageMetadata> = {
  [ROUTES.HOME.path]: {
    title: ROUTES.HOME.title,
    description: ROUTES.HOME.description,
    keywords:
      "mobile healthcare, fee cashback, nigeria healthcare, telemedicine app, doctor consultation, healthcare rewards, medical platform, africa health technology, transparent fees",
    ogImage: "/images/og-home.jpg",
  },
  [ROUTES.ABOUT.path]: {
    title: ROUTES.ABOUT.title,
    description: ROUTES.ABOUT.description,
    keywords:
      "about licomeds, mobile healthcare platform, fee cashback system, african healthcare, telemedicine mission, medical ecosystem, healthcare innovation, nigeria health technology",
    ogImage: "/images/og-about.jpg",
  },
  [ROUTES.FEATURES.path]: {
    title: ROUTES.FEATURES.title,
    description: ROUTES.FEATURES.description,
    keywords:
      "mobile app features, healthcare platform, fee cashback rewards, doctor consultation app, medical app features, health management, nigeria healthcare, transparent fees",
    ogImage: "/images/og-features.jpg",
  },
  [ROUTES.FAQ.path]: {
    title: ROUTES.FAQ.title,
    description: ROUTES.FAQ.description,
    keywords:
      "FAQ, healthcare questions, fee cashback questions, mobile app support, medical platform help, telemedicine support",
    ogImage: "/images/og-faq.jpg",
  },
  [ROUTES.TERMS.path]: {
    title: ROUTES.TERMS.title,
    description: ROUTES.TERMS.description,
    keywords:
      "terms and conditions, fee cashback terms, mobile healthcare agreement, user agreement, healthcare platform terms, legal, licomeds terms",
    ogImage: "/images/og-terms.jpg",
  },
  [ROUTES.PRIVACY.path]: {
    title: ROUTES.PRIVACY.title,
    description: ROUTES.PRIVACY.description,
    keywords:
      "privacy policy, health data protection, medical privacy, mobile app privacy, healthcare data security, patient confidentiality",
    ogImage: "/images/og-privacy.jpg",
  },
  [ROUTES.DONATE.path]: {
    title: ROUTES.DONATE.title,
    description: ROUTES.DONATE.description,
    keywords:
      "healthcare donation, medical aid, community support, healthcare funding, help patients, fee cashback donation, medical assistance",
    ogImage: "/images/og-donate.jpg",
  },
  [ROUTES.BLOG.path]: {
    title: ROUTES.BLOG.title,
    description: ROUTES.BLOG.description,
    keywords:
      "healthcare blog, medical articles, health tips, wellness insights, community stories, patient care blog, healthcare news",
    ogImage: "/images/og-blog.jpg",
  },
};

// Helper functions with proper typing
export const getRouteByPath = (path: string): RouteInfo | undefined => {
  const allRoutes = [
    ROUTES.HOME,
    ROUTES.ABOUT,
    ROUTES.FEATURES,
    ROUTES.FAQ,
    ROUTES.TERMS,
    ROUTES.PRIVACY,
    ROUTES.DONATE,
    ROUTES.CONTACTUS,
    ROUTES.BLOG,
  ];

  return allRoutes.find((route) => route.path === path);
};

export const isExternalUrl = (url: string): boolean => {
  return (
    url.startsWith("http") ||
    url.startsWith("tel:") ||
    url.startsWith("mailto:") ||
    url.startsWith("#")
  );
};

export const getPageMetadata = (path: string): PageMetadata => {
  return PAGE_METADATA[path] || PAGE_METADATA[ROUTES.HOME.path];
};

export const isRouteObject = (
  route: RouteInfo | string
): route is RouteInfo => {
  return typeof route === "object" && "path" in route;
};

export const getRoutePath = (route: RouteInfo | string): string => {
  return isRouteObject(route) ? route.path : route;
};

export const getRouteName = (route: RouteInfo | string): string => {
  return isRouteObject(route) ? route.name : route;
};

// Route validation
export const isValidRoute = (path: any): boolean => {
  const allValidPaths = [
    ROUTES.HOME.path,
    ROUTES.ABOUT.path,
    ROUTES.FEATURES.path,
    ROUTES.FAQ.path,
    ROUTES.TERMS.path,
    ROUTES.PRIVACY.path,
    ROUTES.DONATE.path,
    ROUTES.CONTACTUS.path,
    ROUTES.BLOG.path,
  ];

  return allValidPaths.includes(path);
};

// Social media sharing
export const getSocialShareUrl = (
  platform: "facebook" | "twitter" | "instagram" | "tiktok",
  url: string,
  title: string
): string => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    instagram: ROUTES.EXTERNAL.INSTAGRAM,
    tiktok: ROUTES.EXTERNAL.TIKTOK,
  };

  return shareUrls[platform];
};

// Get app download link based on user agent
export const getAppDownloadLink = (): string => {
  if (typeof window === "undefined") return ROUTES.EXTERNAL.GOOGLE_PLAY;

  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes("iphone") || userAgent.includes("ipad")) {
    return ROUTES.EXTERNAL.APP_STORE;
  } else if (userAgent.includes("android")) {
    return ROUTES.EXTERNAL.GOOGLE_PLAY;
  }

  return ROUTES.EXTERNAL.GOOGLE_PLAY; // Default to Google Play since that's the available app
};

// Check if route is active
export const isActiveRoute = (
  currentPath: string,
  routePath: string
): boolean => {
  if (routePath === ROUTES.HOME.path) {
    return currentPath === routePath;
  }
  return currentPath.startsWith(routePath);
};

// Navigation utilities for smooth scrolling
export const scrollToSection = (
  sectionId: string,
  offset: number = 120
): void => {
  const element = document.querySelector(sectionId);
  if (element) {
    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

// Breadcrumb generation
export const generateBreadcrumbs = (
  pathname: string
): Array<{ name: string; path: string }> => {
  const breadcrumbs: Array<{ name: string; path: string }> = [
    { name: "Home", path: ROUTES.HOME.path },
  ];

  if (pathname === ROUTES.ABOUT.path) {
    breadcrumbs.push({ name: "About Us", path: ROUTES.ABOUT.path });
  } else if (pathname === ROUTES.FEATURES.path) {
    breadcrumbs.push({ name: "Features", path: ROUTES.FEATURES.path });
  } else if (pathname === ROUTES.FAQ.path) {
    breadcrumbs.push({ name: "FAQ", path: ROUTES.FAQ.path });
  } else if (pathname === ROUTES.TERMS.path) {
    breadcrumbs.push({ name: "Terms & Conditions", path: ROUTES.TERMS.path });
  } else if (pathname === ROUTES.PRIVACY.path) {
    breadcrumbs.push({ name: "Privacy Policy", path: ROUTES.PRIVACY.path });
  } else if (pathname === ROUTES.DONATE.path) {
    breadcrumbs.push({ name: "Make a Donation", path: ROUTES.DONATE.path });
  } else if (pathname === ROUTES.CONTACTUS.path) {
    breadcrumbs.push({ name: "Contact Us", path: ROUTES.CONTACTUS.path });
  }

  return breadcrumbs;
};

// SEO and Open Graph utilities
export const generateStructuredData = (route: RouteInfo) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: route.title,
    description: route.description,
    url: `https://licomeds.com${route.path}`,
    publisher: {
      "@type": "Organization",
      name: ROUTES.COMPANY.NAME,
      url: "https://licomeds.com",
      logo: "https://licomeds.com/logo.png",
    },
  };
};

export const TEXT_SNIPPETS = {
  COPYRIGHT: `© 2025 ${ROUTES.COMPANY.FULL_NAME}. Made with ❤️ in Nigeria for Africa.`,
  PLATFORM_NOTE:
    "All healthcare services in mobile app • Website donations via secure fintech partners • Fee cashback rewards",
  SUPPORT_BADGE: "24/7 Mobile Healthcare Support",
  CASHBACK_NOTE: "Earn up to 50% cashback on platform transaction fees",
  MOBILE_FIRST: "Mobile-first healthcare platform with transparent fees",
};
